import type { DealPersonOption } from "./create-deal-helpers";
import type { TriyoProjectTemplate } from "@/types/models";

/**
 * Deal team selection. Mirrors Triyo.Web's project member picker
 * (components/select-member/multiple-owner-select-member-project.jsx): `members`
 * is the full set of people and groups on the Deal, and `owners` is always a
 * SUBSET of it. Groups can be members but never owners.
 */
export interface DealTeamSelection {
  memberKeys: Set<string>;
  ownerKeys: Set<string>;
}

export function emptyDealTeam(): DealTeamSelection {
  return { memberKeys: new Set(), ownerKeys: new Set() };
}

/**
 * Applies a project template's default members and owners using the same rules
 * as Triyo.Web's applyTemplateDefaults helper:
 * - configured owners are users only and are always members;
 * - the signed-in user is always a member;
 * - invalid/missing owner defaults fall back to the signed-in user; and
 * - single-owner templates keep only their first valid owner.
 */
export function buildTemplateTeamSelection(
  options: DealPersonOption[],
  template: TriyoProjectTemplate | null,
  currentUserKey: string | null
): DealTeamSelection {
  const configuredMemberIds = new Set(template?.Members ?? []);
  const configuredOwnerIds = template?.Owners ?? [];
  const memberKeys = new Set(
    options.filter((person) => configuredMemberIds.has(person.id)).map((person) => person.key)
  );

  const validOwnerKeys = configuredOwnerIds
    .map((ownerId) => options.find((person) => !person.isGroup && person.id === ownerId)?.key)
    .filter((key): key is string => !!key);
  const uniqueOwnerKeys = [...new Set(validOwnerKeys)];

  if (uniqueOwnerKeys.length === 0 && currentUserKey && options.some((person) => person.key === currentUserKey)) {
    uniqueOwnerKeys.push(currentUserKey);
  }

  const ownerKeys = new Set(template?.IsMultiOwner === false ? uniqueOwnerKeys.slice(0, 1) : uniqueOwnerKeys);
  ownerKeys.forEach((key) => memberKeys.add(key));

  if (currentUserKey && options.some((person) => person.key === currentUserKey)) {
    memberKeys.add(currentUserKey);
  }

  return { memberKeys, ownerKeys };
}

/**
 * Sort order from Triyo.Web sortProjectMembers: owners first, then other
 * selected members, users ahead of groups, then alphabetical by name.
 */
export function sortDealTeam(options: DealPersonOption[], selection: DealTeamSelection): DealPersonOption[] {
  return [...options].sort((a, b) => {
    const aOwner = selection.ownerKeys.has(a.key);
    const bOwner = selection.ownerKeys.has(b.key);
    if (aOwner !== bOwner) return aOwner ? -1 : 1;

    const aMember = selection.memberKeys.has(a.key);
    const bMember = selection.memberKeys.has(b.key);
    if (aMember !== bMember) return aMember ? -1 : 1;

    if (a.isGroup !== b.isGroup) return a.isGroup ? 1 : -1;

    return a.name.localeCompare(b.name);
  });
}

/**
 * People eligible to own the Deal: selected members that are not groups.
 * Triyo.Web filters groups out of the owner dropdown, so the add-in does too.
 */
export function ownerCandidates(options: DealPersonOption[], selection: DealTeamSelection): DealPersonOption[] {
  return options.filter((person) => !person.isGroup && selection.memberKeys.has(person.key));
}

/**
 * Adds or removes a member. Returns null when the change is blocked, matching
 * Triyo.Web's silent early-returns: the last remaining owner cannot be removed
 * from the Deal. When a non-last owner is deselected they also stop being an
 * owner, and the signed-in user backfills the role if that emptied it.
 */
export function toggleMember(
  selection: DealTeamSelection,
  key: string,
  currentUserKey: string | null
): DealTeamSelection | null {
  const memberKeys = new Set(selection.memberKeys);
  const ownerKeys = new Set(selection.ownerKeys);

  if (!memberKeys.has(key)) {
    memberKeys.add(key);
    return { memberKeys, ownerKeys };
  }

  if (ownerKeys.has(key) && ownerKeys.size === 1) return null;

  memberKeys.delete(key);
  ownerKeys.delete(key);

  if (ownerKeys.size === 0 && currentUserKey && memberKeys.has(currentUserKey)) {
    ownerKeys.add(currentUserKey);
  }

  return { memberKeys, ownerKeys };
}

/**
 * "Select all" adds everyone. Clearing it falls back to the current owners —
 * or the signed-in user when there are none — so the Deal is never left
 * without a member who can own it (Triyo.Web selectAllTypes, create branch).
 */
export function setAllMembers(
  options: DealPersonOption[],
  selection: DealTeamSelection,
  checked: boolean,
  currentUserKey: string | null
): DealTeamSelection {
  if (checked) {
    return { memberKeys: new Set(options.map((person) => person.key)), ownerKeys: new Set(selection.ownerKeys) };
  }

  if (selection.ownerKeys.size > 0) {
    return { memberKeys: new Set(selection.ownerKeys), ownerKeys: new Set(selection.ownerKeys) };
  }

  const fallback = currentUserKey && options.some((person) => person.key === currentUserKey) ? [currentUserKey] : [];
  return { memberKeys: new Set(fallback), ownerKeys: new Set(fallback) };
}

/**
 * Adds or removes an owner. Returns null when blocked: the owner list may not
 * be emptied, and only selected non-group members can be made owners.
 */
export function toggleOwner(
  options: DealPersonOption[],
  selection: DealTeamSelection,
  key: string,
  isMultiOwner = true
): DealTeamSelection | null {
  const candidate = options.find((person) => person.key === key);
  if (!candidate || candidate.isGroup || !selection.memberKeys.has(key)) return null;

  if (!isMultiOwner) {
    return { memberKeys: new Set(selection.memberKeys), ownerKeys: new Set([key]) };
  }

  const ownerKeys = new Set(selection.ownerKeys);
  if (ownerKeys.has(key)) {
    if (ownerKeys.size === 1) return null;
    ownerKeys.delete(key);
    return { memberKeys: new Set(selection.memberKeys), ownerKeys };
  }

  ownerKeys.add(key);
  return { memberKeys: new Set(selection.memberKeys), ownerKeys };
}

/** Selects the signed-in user as the first member and owner, as Triyo.Web does. */
export function seedWithCurrentUser(options: DealPersonOption[], currentUserKey: string | null): DealTeamSelection {
  if (!currentUserKey || !options.some((person) => person.key === currentUserKey)) {
    return emptyDealTeam();
  }
  return { memberKeys: new Set([currentUserKey]), ownerKeys: new Set([currentUserKey]) };
}
